import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { toast } from '@codes/ui-kit/components/ui/use-toast';
import { useQueryClient } from '@tanstack/react-query';
import { createYooptaEditor } from '@yoopta/editor';

import { useLoader } from '@codes/loader';

import { MARKS, TOOLS, plugins, saveSelection } from '../../shared';
import { EditorLayout, EditorMetadata, EditorWrapper, ErrorAlert, TitleInput } from '../components';
import {
    useEditorContent,
    useEditorNotifications,
    useKeyboardShortcuts,
    usePageLeaveBlocker,
    useSaveFocusRestoration,
} from '../hooks';
import { exportContent, updateContentInCache } from '../utils';

import type { Tools, YooEditor } from '@yoopta/editor';

const MAX_TITLE_LENGTH = 50;

export const EditorPage = () => {
    const { setIsLoading } = useLoader();
    const queryClient = useQueryClient();
    const { contentId } = useParams<{ contentId: string }>();

    const { t } = useTranslation(['translation', 'box']);

    const [title, setTitle] = useState<string>('');
    const [metadata, setMetadata] = useState({
        category: '',
        subCategory: '',
        tags: [] as string[],
        description: '',
    });

    const editor = useMemo(() => createYooptaEditor(), []);
    const titleInputRef = useRef<HTMLInputElement>(null);
    const selectionRef = useRef(null);
    const savedSelectionRef = useRef<{ start: number; end: number } | null>(null); // to restore cursor

    // 마지막 저장 시점의 content를 저장
    const lastSavedContentRef = useRef('');
    const hasChangesRef = useRef(false);

    const { notifyLoadError, notifySaveSuccess, notifySaveError } = useEditorNotifications();
    const { content, loading, error, handleSave } = useEditorContent(contentId, editor);
    const { captureFocusState, restoreFocus } = useSaveFocusRestoration(editor, titleInputRef);

    const editorConfig = useMemo(
        () => ({
            plugins,
            tools: TOOLS as Partial<Tools>,
            marks: MARKS,
        }),
        []
    );

    const focusBlockWithOptions = useCallback(
        (editor: YooEditor, blockId: string) => {
            if (!editor || !blockId) {
                console.log('Invalid editor or blockId');
                return;
            }

            const performFocus = () => {
                try {
                    const baseOptions = {
                        waitExecution: true,
                        waitExecutionMs: 0,
                    };

                    const isValidSelection =
                        savedSelectionRef.current &&
                        typeof savedSelectionRef.current.start === 'number' &&
                        typeof savedSelectionRef.current.end === 'number';

                    if (isValidSelection && savedSelectionRef.current!.start === savedSelectionRef.current!.end) {
                        const focusOptions = {
                            ...baseOptions,
                            focusAt: {
                                path: [0],
                                offset: savedSelectionRef.current!.end,
                            },
                        };
                        editor.focusBlock(blockId, focusOptions);
                    } else {
                        editor.focusBlock(blockId, baseOptions);
                    }
                } catch (innerError) {
                    console.log('Focus block failed in timeout:', innerError);
                    editor.focus();
                } finally {
                    savedSelectionRef.current = null;
                }
            };

            try {
                setTimeout(performFocus, 0);
            } catch (error) {
                console.log('Focus block failed:', error);
                editor.focus();
            }
        },
        [savedSelectionRef]
    );

    useEffect(() => {
        if (error) {
            notifyLoadError(error);
        }
    }, [error, notifyLoadError]);

    // 컨텐츠가 처음 로드될 때 lastSavedContent 초기화
    useEffect(() => {
        if (!content) {
            setTitle('');
            setMetadata({
                category: '',
                subCategory: '',
                tags: [],
                description: '',
            });
            return;
        }
        setTitle(content.title || '');
        setMetadata({
            category: content.category || '',
            subCategory: content.subCategory || '',
            tags: content.tags || [],
            description: content.description || '',
        });
        // 저장 상태 초기화
        const currentContent = editor.getEditorValue();
        lastSavedContentRef.current = JSON.stringify(currentContent);
        hasChangesRef.current = false;
    }, [content, editor]);

    const checkForChanges = useCallback(() => {
        const currentContent = editor.getEditorValue();
        const currentContentStr = JSON.stringify(currentContent);
        const hasContentChanges = currentContentStr !== lastSavedContentRef.current;
        const hasTitleChanges = title !== content?.title;
        const hasMetadataChanges =
            metadata.category !== (content?.category || '') ||
            metadata.subCategory !== (content?.subCategory || '') ||
            JSON.stringify(metadata.tags) !== JSON.stringify(content?.tags || []) ||
            metadata.description !== (content?.description || '');

        return hasContentChanges || hasTitleChanges || hasMetadataChanges;
    }, [
        editor,
        title,
        content?.title,
        content?.category,
        content?.subCategory,
        content?.tags,
        content?.description,
        metadata,
    ]);

    // 페이지 이탈 방지
    usePageLeaveBlocker(hasChangesRef.current, checkForChanges);

    useEffect(() => {
        setIsLoading(loading);
        return () => setIsLoading(false);
    }, [loading, setIsLoading]);

    useEffect(() => {
        if (loading) {
            savedSelectionRef.current = saveSelection();
            editor.blur();
        } else {
            // 초기 로드 시에만 포커스 처리
            if (titleInputRef.current && (!content?.title || !content?.element$$?.length)) {
                titleInputRef.current.focus();
                titleInputRef.current.select();
            } else {
                editor.focus();
            }
        }
    }, [loading, editor, content?.title, content?.element$$?.length]);

    const prepareSave = useCallback(() => {
        if (!checkForChanges()) {
            return null;
        }

        const focusState = captureFocusState();
        return { focusState };
    }, [checkForChanges, captureFocusState]);

    const updateLocalState = useCallback(() => {
        const currentContent = editor.getEditorValue();
        lastSavedContentRef.current = JSON.stringify(currentContent);
        hasChangesRef.current = false;
        return currentContent;
    }, [editor]);

    const updateUI = useCallback(
        (currentContent: any, focusState: any) => {
            if (contentId) {
                updateContentInCache(queryClient, contentId, { title });
            }
            notifySaveSuccess(title);
            restoreFocus(focusState, currentContent, focusBlockWithOptions);
        },
        [contentId, title, notifySaveSuccess, restoreFocus]
    );

    const handleSaveError = useCallback(
        (error: unknown) => {
            notifySaveError(error);
        },
        [notifySaveError]
    );

    const saveContent = useCallback(async () => {
        const saveContext = prepareSave();
        if (!saveContext) {
            return;
        }

        const { focusState } = saveContext;

        try {
            await handleSave(title, metadata);
            const currentContent = updateLocalState();
            updateUI(currentContent, focusState);
        } catch (error) {
            handleSaveError(error);
        }
    }, [prepareSave, handleSave, title, metadata, updateLocalState, updateUI, handleSaveError]);

    const handleMetadataChange = useCallback(
        (metadataData: { category: string; subCategory: string; tags: string[]; description: string }) => {
            setMetadata(metadataData);
            hasChangesRef.current = true;
        },
        []
    );

    // 에디터 변경 감지는 항상 동작하도록
    useEffect(() => {
        const handleEditorChange = () => {
            hasChangesRef.current = true;
        };

        editor.on('change', handleEditorChange);
        return () => {
            editor.off('change', handleEditorChange);
        };
    }, [editor]);

    // 메타데이터 변경 감지
    useEffect(() => {
        if (content) {
            const hasMetadataChanges =
                metadata.category !== (content?.category || '') ||
                metadata.subCategory !== (content?.subCategory || '') ||
                JSON.stringify(metadata.tags) !== JSON.stringify(content?.tags || []) ||
                metadata.description !== (content?.description || '');

            if (hasMetadataChanges) {
                hasChangesRef.current = true;
            }
        }
    }, [metadata, content]);

    useKeyboardShortcuts([{ key: 's', ctrlOrMeta: true, handler: saveContent }]);

    const titleHandlers = useMemo(
        () => ({
            onChange: (newTitle: string) => {
                setTitle(newTitle);
                hasChangesRef.current = true;
            },
            onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
                if (e.key === 'Enter') {
                    e.preventDefault();
                    editor.focus();
                }
            },
        }),
        [editor]
    );

    const handleClickExport = useCallback(
        async (type: 'markdown' | 'html') => {
            try {
                const { filename } = exportContent({ type, editor, title });

                toast({
                    title: t('box:editorPage.export.complete'),
                    description: t(`box:editorPage.export.${type}`),
                });
            } catch (error) {
                console.error('Export failed:', error);
                toast({
                    variant: 'destructive',
                    title: t('box:editorPage.export.failed'),
                    description: `${error.toString()}`,
                });
            }
        },
        [title, editor, t]
    );

    const actionHandlers = useMemo(
        () => ({
            save: saveContent,
            export: handleClickExport,
        }),
        [saveContent, handleClickExport]
    );

    return (
        <>
            <ErrorAlert
                error={error}
                errorMessage={t('box:editorPage.load.error')}
                retryLabel={t('box:editorPage.load.refresh')}
            />
            <EditorLayout
                title={title}
                isLoading={loading}
                contentId={contentId}
                content={content}
                handleSave={actionHandlers.save}
                handleExport={actionHandlers.export}
            >
                <div className="px-20 py-6 max-md:px-[50px] w-full flex flex-col justify-center max-w-screen-xl mx-auto">
                    <EditorMetadata content={content} onMetadataChange={handleMetadataChange} />
                    <TitleInput
                        ref={titleInputRef}
                        value={title}
                        onChange={titleHandlers.onChange}
                        onKeyDown={titleHandlers.onKeyDown}
                        placeholder={t('box:editorPage.newPage')}
                        maxLength={MAX_TITLE_LENGTH}
                    />
                    <EditorWrapper ref={selectionRef} editor={editor} config={editorConfig} />
                </div>
            </EditorLayout>
        </>
    );
};
